package io.horion.cv.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.styles.FooterBrandContainerStyle
import io.horion.cv.styles.FooterBrandLogoStyle
import io.horion.cv.styles.FooterBrandTextStyle
import io.horion.cv.styles.FooterCopyrightStyle
import io.horion.cv.styles.FooterDesktopSectionStyle
import io.horion.cv.styles.FooterDividerStyle
import io.horion.cv.styles.FooterLinkColumnStyle
import io.horion.cv.styles.FooterMainContainerStyle
import io.horion.cv.styles.FooterMobileSectionStyle
import io.horion.cv.styles.NavigationLinkStyle
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import org.jetbrains.compose.web.css.px

@OptIn(DelicateApi::class)
@Composable
fun Footer() {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val footerBg = if (isDark) Colors.BlackLight else Colors.DarkBlue

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(footerBg)
            .styleModifier { property("transition", "background-color 300ms ease") }
            .padding(top = 30.px, bottom = 20.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = FooterMainContainerStyle.toModifier(),
            contentAlignment = Alignment.Center
        ) {
            // Affichage Mobile (piloté par CSS)
            Column(
                modifier = FooterMobileSectionStyle.toModifier().fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(30.px)
            ) {
                FooterBrand()

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly,
                    verticalAlignment = Alignment.Top
                ) {
                    FooterLinkComponent(
                        links = listOf(
                            "Services" to "/services",
                            "Créer un CV" to "/choose-cv",
                            "Lettre de motivation" to "/motivation",
                            "Modèles de lettre\nde motivation" to "/motivation",
                            //"Offres d'emplois" to "/job",
                            "Candidatures" to "/candidatures",
                        )
                    )
                    FooterLinkComponent(
                        links = listOf(
                            //"Prix" to "/price",
                            "Contact" to "/contact",
                            "Conditions générales" to "/conditions",
                            "Politique de confidentialité" to "/confidentialite",
                            "À propos" to "/about",
                            "FAQ" to "/faq",
                            "Avis" to "/avis",
                        )
                    )
                }
            }

            // Affichage Desktop (piloté par CSS)
            Row(
                modifier = FooterDesktopSectionStyle.toModifier().fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                FooterBrand()

                FooterLinkComponent(
                    links = listOf(
                        "Services" to "/services",
                        "Créer un CV" to "/choose-cv",
                        "Lettre de motivation" to "/motivation",
                        "Modèles de lettre de motivation" to "/modeles-lettre",
                        //"Candidatures" to "/candidatures",
                    )
                )

                FooterLinkComponent(
                    links = listOf(
                        //"Offres d'emploi" to "/job",
                        "Contact" to "/contact",
                        "Conditions générales" to "/conditions",
                        "Politique de confidentialité" to "/confidentialite"
                    )
                )

                FooterLinkComponent(
                    links = listOf(
                        "À propos" to "/about",
                        "Avis" to "/avis",
                        "FAQ" to "/faq"
                    )
                )
            }
        }

        Box(
            modifier = FooterDividerStyle.toModifier()
                .opacity(if (isDark) 0.4 else 0.29)
                .backgroundColor(Colors.LightGrayLine)
        )

        FooterCopyright()
    }
}

@Composable
fun FooterBrand() {
    Column(
        modifier = FooterBrandContainerStyle.toModifier(),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(15.px)
    ) {
        Image(
            src = "/logo-CV-new-blanc.png",
            description = "Logo image",
            modifier = FooterBrandLogoStyle.toModifier()
        )

        SpanText(
            "L'accès facile à la documentation\npour chercheur d'emploi.",
            modifier = FooterBrandTextStyle.toModifier().color(Colors.White)
        )
    }
}

@Composable
fun FooterLinkComponent(
    links: List<Pair<String, String>>,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.then(FooterLinkColumnStyle.toModifier()),
        verticalArrangement = Arrangement.Top
    ) {
        links.forEach { (title, path) ->
            NavigationLink(
                title = title,
                path = path,
            )
        }
    }
}

@Composable
fun NavigationLink(
    title: String,
    path: String,
) {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val linkColor = if (isDark) Colors.LightGray else Colors.White

    Link(
        path = path,
        text = title,
        modifier = NavigationLinkStyle.toModifier()
            .color(linkColor)
            .fontFamily(INTER)
            .textDecorationLine(TextDecorationLine.None)
    )
}

@Composable
fun FooterCopyright() {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val copyrightColor = if (isDark) Colors.LightGray else Colors.White

    SpanText(
        "© 2026 Orion. Tous droits réservés.",
        modifier = FooterCopyrightStyle.toModifier().color(copyrightColor)
    )
}